import { FormattedNumber } from 'react-intl';
import CountUp from 'react-countup';
import { DEFAULT_LOCALE, useLocale } from '../i18n/locale';
import classNames from 'classnames';

const NARROW_SYMBOL_CURRENCIES = ['EUR', 'USD', 'GBP'];

export const getCurrencyDisplay = (currency: string) => {
  return NARROW_SYMBOL_CURRENCIES.includes(currency) ? 'narrowSymbol' : 'code';
};

export const monetaryValueOptions = (
  currency: string,
  maximumFractionDigits: number = 2,
): {
  currency: string;
  style: 'currency';
  currencyDisplay: 'narrowSymbol' | 'code';
  maximumFractionDigits: number;
} => {
  return {
    style: 'currency',
    currency,
    currencyDisplay: getCurrencyDisplay(currency),
    maximumFractionDigits,
  };
};

type MonetaryValueProps = {
  amount: number;
  currency: string;
  maximumFractionDigits?: number;
  countUp?: boolean;
  strikethrough?: boolean;
};

export const MonetaryValue = ({
  amount,
  currency,
  maximumFractionDigits = 2,
  countUp = false,
  strikethrough = false,
}: MonetaryValueProps) => {
  const { locale } = useLocale();

  const currencySymbol = Intl.NumberFormat(DEFAULT_LOCALE, {
    style: 'currency',
    currencyDisplay: getCurrencyDisplay(currency),
    currency,
  })
    .formatToParts(0)
    .filter((part) => part.type === 'currency')
    .map((part) => part.value)
    .join('');

  if (!isFinite(amount) || isNaN(amount)) {
    return `${currencySymbol} -`;
  }

  const options = monetaryValueOptions(currency, maximumFractionDigits);

  return (
    <span
      className={classNames({
        'text-decoration-line-through': strikethrough,
      })}
    >
      {countUp ? (
        <CountUp
          useEasing
          end={amount}
          decimals={maximumFractionDigits}
          formattingFn={(value) => {
            return new Intl.NumberFormat(locale, {
              ...options,
            }).format(value);
          }}
        />
      ) : (
        <FormattedNumber
          value={amount}
          {...options}
        />
      )}
    </span>
  );
};
